export const supportedMimeTypes: `${string}/${string}`[] = [
    "image/bmp",
    "text/csv",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "message/rfc822",
    "application/epub+zip",
    "image/heic",
    "image/jpeg",
    "image/png",
    "text/markdown",
    "application/vnd.ms-outlook",
    "application/vnd.oasis.opendocument.text",
    "text/org",
    "application/pkcs7-signature",
    "application/pdf",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/x-rst",
    "application/rtf",
    "image/tiff",
    "text/plain",
    "text/tab-separated-values",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/xml"
];

const extensionToMimeType: Record<string, `${string}/${string}`> = {
    "bmp": "image/bmp",
    "csv": "text/csv",
    "doc": "application/msword",
    "docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "eml": "message/rfc822",
    "epub": "application/epub+zip",
    "heic": "image/heic",
    "jpeg": "image/jpeg",
    "jpg": "image/jpeg",
    "markdown": "text/markdown",
    "md": "text/markdown",
    "msg": "application/vnd.ms-outlook",
    "odt": "application/vnd.oasis.opendocument.text",
    "org": "text/org",
    "p7s": "application/pkcs7-signature",
    "pdf": "application/pdf",
    "png": "image/png",
    "ppt": "application/vnd.ms-powerpoint",
    "pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "rst": "text/x-rst",
    "rtf": "application/rtf",
    "tif": "image/tiff",
    "tiff": "image/tiff",
    "tsv": "text/tab-separated-values",
    "txt": "text/plain",
    "xls": "application/vnd.ms-excel",
    "xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "xml": "application/xml"
};

const mimeTypeToExtension = Object.fromEntries(
    Object.entries(extensionToMimeType).map(([extension, mimeType]) => [mimeType, extension])
) as Record<string, string>;

const unreliableMimeTypes = new Set(["", "application/octet-stream"]);

export const getFileExtension = (fileName: string): string => {
    return fileName.substring(fileName.lastIndexOf(".") + 1).toLowerCase();
};

export const getFileExtensionFromMimeType = (mimeType: string): string | undefined => {
    const normalizedMimeType = mimeType.split(";", 1)[0].trim().toLowerCase();

    return mimeTypeToExtension[normalizedMimeType];
};

// Some extensions (notably `.md` on Windows) have no registered MIME type, so the
// browser reports an empty or `application/octet-stream` value on `file.type` and
// the Valibot `mimeType()` check rejects the file. Derive the MIME from the
// extension in that case so validation and the upload Content-Type are correct.
export const normalizeFileMimeType = (file: File): File => {
    if (!unreliableMimeTypes.has(file.type)) {
        return file;
    }

    const derivedMimeType = extensionToMimeType[getFileExtension(file.name)];

    if (!derivedMimeType) {
        return file;
    }

    return new File([file], file.name, { type: derivedMimeType, lastModified: file.lastModified });
};
